import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { documentService } from '../../services/documentService';

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const { user, profile, updateProfile, isProfileComplete, hasRequiredDocuments } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Medical Information Form
  const [medicalInfo, setMedicalInfo] = useState({
    licenseNumber: '',
    specialization: '',
    institutionName: '',
    institutionCountry: '',
    phoneNumber: '',
  });

  // Document Upload
  const [documents, setDocuments] = useState({
    license: null,
    certification: null,
  });
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    // Pre-fill existing data
    if (profile) {
      setMedicalInfo({
        licenseNumber: profile?.license_number || '',
        specialization: profile?.specialization || '',
        institutionName: profile?.institution_name || '',
        institutionCountry: profile?.institution_country || '',
        phoneNumber: profile?.phone_number || '',
      });
    }
  }, [profile]);

  const handleMedicalInfoChange = (e) => {
    const { name, value } = e?.target;
    setMedicalInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMedicalInfoSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      await updateProfile({
        license_number: medicalInfo?.licenseNumber,
        specialization: medicalInfo?.specialization,
        institution_name: medicalInfo?.institutionName,
        institution_country: medicalInfo?.institutionCountry,
        phone_number: medicalInfo?.phoneNumber,
      });

      setSuccess('Medical information saved successfully');
      setTimeout(() => {
        setStep(2);
        setSuccess('');
      }, 1500);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err?.message || 'Failed to save medical information');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentChange = (e, type) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setDocuments((prev) => ({
        ...prev,
        [type]: file,
      }));
    }
  };

  const handleDocumentUpload = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      const uploads = [];

      if (documents?.license) {
        uploads?.push(
          documentService?.uploadDocument(
            user?.id,
            documents?.license,
            'medical_license',
            (progress) => {
              setUploadProgress((prev) => ({ ...prev, license: progress }));
            }
          )
        );
      }

      if (documents?.certification) {
        uploads?.push(
          documentService?.uploadDocument(
            user?.id,
            documents?.certification,
            'professional_certification',
            (progress) => {
              setUploadProgress((prev) => ({ ...prev, certification: progress }));
            }
          )
        );
      }

      await Promise.all(uploads);

      setSuccess('Documents uploaded successfully. Awaiting admin approval.');
      setTimeout(() => {
        navigate('/pending-approval');
      }, 2000);
    } catch (err) {
      console.error('Error uploading documents:', err);
      setError(err?.message || 'Failed to upload documents');
    } finally {
      setLoading(false);
      setUploadProgress({});
    }
  };

  return (
    <>
      <Helmet>
        <title>Complete Your Profile - DiagnosticAI</title>
      </Helmet>
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="clinical-card p-8">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="ClipboardList" size={32} color="var(--color-primary)" />
                <h1 className="text-3xl font-bold text-foreground">Complete Your Profile</h1>
              </div>
              <p className="text-muted-foreground">
                Please provide your medical credentials and upload required documents to proceed.
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8 flex items-center gap-4">
              <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-muted'}`}></div>
              <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-muted'}`}></div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
                <Icon name="AlertCircle" size={18} color="var(--color-error)" />
                <div>
                  <p className="text-sm font-medium text-error">Error</p>
                  <p className="text-xs text-muted-foreground mt-1">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
                <Icon name="CheckCircle" size={18} color="var(--color-success)" />
                <p className="text-sm font-medium text-success">{success}</p>
              </div>
            )}

            {/* Step 1: Medical Information */}
            {step === 1 && (
              <form onSubmit={handleMedicalInfoSubmit} className="space-y-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Medical Credentials</h2>

                <Input
                  label="Medical License Number"
                  name="licenseNumber"
                  value={medicalInfo?.licenseNumber}
                  onChange={handleMedicalInfoChange}
                  placeholder="e.g., MD-123456"
                  required
                  disabled={loading}
                />

                <Input
                  label="Specialization"
                  name="specialization"
                  value={medicalInfo?.specialization}
                  onChange={handleMedicalInfoChange}
                  placeholder="e.g., Radiology, Cardiology"
                  required
                  disabled={loading}
                />

                <Input
                  label="Institution Name"
                  name="institutionName"
                  value={medicalInfo?.institutionName}
                  onChange={handleMedicalInfoChange}
                  placeholder="e.g., City General Hospital"
                  required
                  disabled={loading}
                />

                <Input
                  label="Institution Country"
                  name="institutionCountry"
                  value={medicalInfo?.institutionCountry}
                  onChange={handleMedicalInfoChange}
                  placeholder="e.g., United States"
                  required
                  disabled={loading}
                />

                <Input
                  label="Phone Number"
                  name="phoneNumber"
                  type="tel"
                  value={medicalInfo?.phoneNumber}
                  onChange={handleMedicalInfoChange}
                  placeholder="e.g., +1 (555) 123-4567"
                  required
                  disabled={loading}
                />

                <Button type="submit" fullWidth loading={loading} iconName="ArrowRight" iconPosition="right">
                  Continue to Document Upload
                </Button>
              </form>
            )}

            {/* Step 2: Document Upload */}
            {step === 2 && (
              <form onSubmit={handleDocumentUpload} className="space-y-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Upload Documents</h2>

                <div className="space-y-4">
                  <div className="border-2 border-dashed border-border rounded-lg p-6 hover:border-primary transition-colors">
                    <label className="block cursor-pointer">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon name="FileText" size={24} color="var(--color-primary)" />
                        <span className="font-medium text-foreground">Medical License</span>
                        <span className="text-xs text-error">*Required</span>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentChange(e, 'license')}
                        className="hidden"
                        disabled={loading}
                      />
                      {documents?.license ? (
                        <p className="text-sm text-success flex items-center gap-2">
                          <Icon name="Check" size={16} />
                          {documents?.license?.name}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">Click to upload (PDF, JPG, PNG)</p>
                      )}
                      {uploadProgress?.license > 0 && (
                        <div className="mt-2 w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress?.license}%` }}
                          ></div>
                        </div>
                      )}
                    </label>
                  </div>

                  <div className="border-2 border-dashed border-border rounded-lg p-6 hover:border-primary transition-colors">
                    <label className="block cursor-pointer">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon name="Award" size={24} color="var(--color-primary)" />
                        <span className="font-medium text-foreground">Professional Certification</span>
                        <span className="text-xs text-muted-foreground">Optional</span>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentChange(e, 'certification')}
                        className="hidden"
                        disabled={loading}
                      />
                      {documents?.certification ? (
                        <p className="text-sm text-success flex items-center gap-2">
                          <Icon name="Check" size={16} />
                          {documents?.certification?.name}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">Click to upload (PDF, JPG, PNG)</p>
                      )}
                      {uploadProgress?.certification > 0 && (
                        <div className="mt-2 w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress?.certification}%` }}
                          ></div>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    disabled={loading}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    fullWidth
                    loading={loading}
                    disabled={!documents?.license}
                    iconName="Upload"
                    iconPosition="right"
                  >
                    Submit for Review
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileCompletion;